import { Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommentsService } from '../../service/comments.service';
import { ExcelImportService, SchemaNode, SpreadsheetRows } from '../../service/excel-import.service';
import { SettingsService } from '../../service/settings.service';
import { TridyQrErrorCorrection, TridyQrExportService, TridyQrExportSettings, TridyQrFormat } from '../../service/tridy-qr-export.service';
import { ImportedTridy, TridysService } from '../../service/tridys.service';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx/xlsx.mjs';
import { ColDef, iconSetQuartzLight, themeQuartz } from 'ag-grid-community';
import { AG_GRID_LOCALE_DE, AG_GRID_LOCALE_EN, AG_GRID_LOCALE_ES } from '@ag-grid-community/locale';
import { insertQrFilenamePlaceholder, qrFilenameExtension, qrFilenameKeyLabel, qrFilenameKeyParent, qrFilenameSchemaKeys, qrFilenameTemplateError, QR_FILENAME_DATE_KEYS } from '../qr-filename-template';
import { localizeTridyImportError } from '../tridy-import-translation';

interface ImportRow {
  rowNumber: number;
  payload: Record<string, unknown>;
  errors: string[];
}

@Component({
  selector: 'app-generate-tridys',
  standalone: false,
  templateUrl: './generate-tridys.component.html',
  styleUrls: ['./generate-tridys.component.css']
})
export class GenerateTridysComponent {
  contentTypes: any[] = [];
  contentTypeId = '';
  contentTypeSearchTerm = '';
  spreadsheet?: SpreadsheetRows;
  rows: ImportRow[] = [];
  headerErrors: string[] = [];
  importing = false;
  importProgress = { completed: 0, total: 0 };
  generatingQr = false;
  qrProgress = { completed: 0, total: 0 };
  qrFormat: TridyQrFormat = 'cmyk-pdf';
  qrWidthMm = 30;
  qrDpi = 300;
  qrQuietZoneModules = 4;
  qrErrorCorrection: TridyQrErrorCorrection = 'M';
  qrJpegQuality = 95;
  qrFilenamePrefix = 'dpp-qr-codes';
  filenameTemplate = '';
  readonly dateFilenameKeys = QR_FILENAME_DATE_KEYS;
  readonly filenameKeyLabel = qrFilenameKeyLabel;
  readonly filenameKeyParent = qrFilenameKeyParent;
  createdTridys: ImportedTridy[] = [];
  qrDownloadFailed = false;
  tridyCreateLimit = 1000;
  tridyCreatedToday?: number;
  checkingQuota = false;
  currentStep: 1 | 2 | 3 = 1;
  validationGridRows: any[] = [];
  validationColumnDefs: ColDef[] = [];
  showFullFieldPaths = false;
  readonly validationPageSizeSelector = [25, 50, 100];
  agGridLocaleText = AG_GRID_LOCALE_EN;
  gridLanguage = 'en';
  readonly gridTheme = themeQuartz.withPart(iconSetQuartzLight).withParams({
    backgroundColor: '#ffffff',
    headerBackgroundColor: '#f8fafc',
    foregroundColor: 'rgb(46, 55, 66)',
    borderColor: '#d9e1ea',
    oddRowBackgroundColor: '#fbfcfe',
    headerFontSize: 12,
    headerFontWeight: 650,
    spacing: 6,
    rowBorder: true,
    wrapperBorder: false,
    wrapperBorderRadius: 0,
    fontFamily: 'Roboto, sans-serif'
  });
  readonly validationDefaultColDef: ColDef = {
    sortable: true,
    resizable: true,
    filter: true,
    minWidth: 140,
    valueFormatter: params => this.formatGridValue(params.value),
    tooltipValueGetter: params => this.formatGridValue(params.value)
  };

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private settingsService: SettingsService,
    private excelImportService: ExcelImportService,
    private tridysService: TridysService,
    private tridyQrExportService: TridyQrExportService,
    private commentsService: CommentsService,
    private translate: TranslateService,
    private destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.updateLanguage(this.translate.currentLang() ?? 'en');
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(change => this.updateLanguage(change.lang));
    this.contentTypeId = this.route.snapshot.queryParamMap.get('contentTypeId') || '';
    this.contentTypes = this.settingsService.settingsContentTypesWithId || [];
    if (!this.contentTypes.length) {
      this.settingsService.observeContextsContentTypesFirestoreWithId(this.settingsService.contextId$)
        .subscribe(contentTypes => this.contentTypes = contentTypes || []);
    }
    this.settingsService.selectedContextDataChange$.subscribe(() => this.refreshDailyTridyCreateAllowance());
    this.refreshDailyTridyCreateAllowance();
  }

  private updateLanguage(language: string): void {
    this.gridLanguage = language.startsWith('de') ? 'de' : language.startsWith('es') ? 'es' : 'en';
    this.agGridLocaleText = language.startsWith('de') ? AG_GRID_LOCALE_DE
      : language.startsWith('es') ? AG_GRID_LOCALE_ES : AG_GRID_LOCALE_EN;
  }

  private get dateLocale(): string {
    const language = this.translate.currentLang() ?? 'en';
    return language.startsWith('de') ? 'de-DE' : language.startsWith('es') ? 'es-ES' : 'en-GB';
  }

  localizeValidationError(error: string): string {
    if (error === 'Diese DPP Vorlage hat noch keine Datenstruktur.') {
      return this.translate.instant('tridys.importWizard.noSchema');
    }
    const unknownColumns = error.match(/^Diese Spalten passen nicht zur Datenstruktur der gewählten DPP Vorlage: (.*)\.$/);
    if (unknownColumns) {
      return this.translate.instant('tridys.generation.unknownColumns', { columns: unknownColumns[1] });
    }
    return localizeTridyImportError(error, this.translate);
  }

  async readFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    try {
      this.spreadsheet = await this.excelImportService.readSpreadsheet(file);
      this.validateRows();
      this.currentStep = 2;
    } catch (error: any) {
      this.spreadsheet = undefined;
      this.rows = [];
      this.refreshValidationGrid();
      this.commentsService.addSnackBar.emit(error?.message
        ? localizeTridyImportError(error.message, this.translate)
        : this.translate.instant('tridys.importWizard.fileReadFailed'));
    } finally {
      input.value = '';
    }
  }

  validateRows(): void {
    if (!this.spreadsheet || !this.contentTypeId) {
      this.rows = [];
      this.headerErrors = [];
      this.refreshValidationGrid();
      return;
    }
    this.headerErrors = [];
    const schema = this.selectedContentType?.schema?.properties as SchemaNode[] | undefined;
    if (!schema?.length) {
      this.rows = this.spreadsheet.rows.map(row => ({ rowNumber: row.rowNumber, payload: {}, errors: ['Diese DPP Vorlage hat noch keine Datenstruktur.'] }));
      this.refreshValidationGrid();
      this.refreshDailyTridyCreateAllowance();
      return;
    }
    const schemaKeys = this.excelImportService.getImportableSchemaKeys(schema);
    const unknownHeaders = this.spreadsheet.headers.filter(header => !schemaKeys.includes(header));
    this.headerErrors = unknownHeaders.length ? [`Diese Spalten passen nicht zur Datenstruktur der gewählten DPP Vorlage: ${unknownHeaders.join(', ')}.`] : [];
    this.rows = this.spreadsheet.rows.map(row => {
      const result = this.excelImportService.validatePayload(row.values, schema);
      return { rowNumber: row.rowNumber, payload: result.payload, errors: result.errors };
    });
    this.refreshValidationGrid();
    this.refreshDailyTridyCreateAllowance();
  }

  async importValidRows(): Promise<void> {
    const validRows = this.rows.filter(row => !row.errors.length);
    if (!this.contentTypeId || !this.spreadsheet || !validRows.length || this.importing || this.createdTridys.length) return;
    await this.refreshDailyTridyCreateAllowance();
    if (this.tridyCreatedToday === undefined) {
      this.commentsService.addSnackBar.emit(this.translate.instant('tridys.generation.quotaCheckFailed'));
      return;
    }
    if (this.remainingTridyCreations < validRows.length) {
      this.commentsService.addSnackBar.emit(this.translate.instant('tridys.generation.quotaInsufficient', {
        remaining: this.remainingTridyCreations, count: validRows.length
      }));
      return;
    }
    this.importing = true;
    this.importProgress = { completed: 0, total: validRows.length };
    try {
      this.commentsService.progressSpin.emit(true);
      const created = await this.tridysService.createImportedTridys(
        this.contentTypeId,
        validRows.map(row => row.payload),
        this.spreadsheet.fileName,
        (completed, total) => this.importProgress = { completed, total }
      );
      this.createdTridys = created;
      this.commentsService.addSnackBar.emit(this.translate.instant('tridys.generation.createdCount', { count: created.length }));
      this.currentStep = 3;
    } catch (error: any) {
      this.commentsService.addSnackBar.emit(error?.message || this.translate.instant('tridys.generation.createFailed'));
    } finally {
      this.commentsService.progressSpin.emit(false);
      this.importing = false;
    }
  }

  get selectedContentType(): any { return this.contentTypes.find(contentType => contentType.id === this.contentTypeId); }
  get qrFilenameKeys(): string[] { return qrFilenameSchemaKeys(this.selectedContentType?.schema); }
  get filenameTemplateError(): string | undefined { return qrFilenameTemplateError(this.filenameTemplate, 'de'); }
  get filenameExtension(): string { return qrFilenameExtension(this.qrFormat); }
  insertFilenameKey(key: string, input: HTMLInputElement): void {
    this.filenameTemplate = insertQrFilenamePlaceholder(this.filenameTemplate, key, input);
  }

  get filteredContentTypes(): any[] {
    const term = this.contentTypeSearchTerm.trim().toLowerCase();
    if (!term) return this.contentTypes;
    return this.contentTypes.filter(contentType =>
      String(contentType?.displayName || '').toLowerCase().includes(term)
        || String(contentType?.id || '').toLowerCase().includes(term)
    );
  }
  get validCount(): number { return this.headerErrors.length ? 0 : this.rows.filter(row => !row.errors.length).length; }
  get invalidCount(): number { return this.rows.length - this.validCount; }
  private refreshValidationGrid(): void {
    const rawRows = this.spreadsheet?.rows || [];
    this.validationGridRows = this.rows.map((row, index) => ({
      __rowNumber: row.rowNumber,
      __errors: row.errors,
      ...(rawRows[index]?.values || {})
    }));

    const headers = this.spreadsheet?.headers || [];
    const unknownHeaders = new Set(this.headerErrors.length ? headers.filter(header => !this.templateHeaders.includes(header)) : []);
    this.validationColumnDefs = [
      ...headers.map(header => ({
        field: header,
        valueGetter: params => params.data?.[header],
        headerName: this.showFullFieldPaths ? header : this.shortFieldPath(header),
        headerTooltip: header,
        wrapHeaderText: this.showFullFieldPaths,
        autoHeaderHeight: this.showFullFieldPaths,
        width: 190,
        minWidth: 140,
        headerClass: unknownHeaders.has(header) ? 'grid-header-invalid' : undefined,
        cellClassRules: {
          'grid-cell-invalid': params => this.isInvalidGridCell(params.data, header, unknownHeaders)
        },
        tooltipValueGetter: params => this.gridCellTooltip(params.data, header, params.value, unknownHeaders)
      } as ColDef))
    ];
  }

  setFullFieldPaths(show: boolean): void {
    this.showFullFieldPaths = show;
    this.validationColumnDefs = this.validationColumnDefs.map(column => ({
      ...column,
      headerName: show ? column.field : this.shortFieldPath(column.field || ''),
      wrapHeaderText: show,
      autoHeaderHeight: show
    }));
  }

  private shortFieldPath(path: string): string {
    const parts = path.split('.');
    return parts.length > 1 ? `… ${parts[parts.length - 1]}` : path;
  }
  get remainingTridyCreations(): number { return Math.max(0, this.tridyCreateLimit - (this.tridyCreatedToday || 0)); }
  get quotaMessage(): string {
    if (this.checkingQuota || this.tridyCreatedToday === undefined) return this.translate.instant('tridys.generation.checkingQuota');
    return this.translate.instant('tridys.generation.quotaAvailable', {
      remaining: this.remainingTridyCreations, limit: this.tridyCreateLimit
    });
  }
  get importPercent(): number {
    if (!this.importProgress.total) return 0;
    return Math.round((this.importProgress.completed / this.importProgress.total) * 100);
  }
  get qrPercent(): number {
    if (!this.qrProgress.total) return 0;
    return Math.round((this.qrProgress.completed / this.qrProgress.total) * 100);
  }
  get importDisabledReason(): string | undefined {
    if (this.importing) return this.translate.instant('tridys.generation.creatingNow');
    if (this.generatingQr) return this.translate.instant('tridys.generation.creatingQrNow');
    if (this.createdTridys.length) return this.translate.instant('tridys.generation.fileAlreadyProcessed');
    if (!this.contentTypeId) return this.translate.instant('tridys.importWizard.selectTemplateFirst');
    if (!this.spreadsheet) return this.translate.instant('tridys.importWizard.selectSpreadsheetFirst');
    if (this.checkingQuota || this.tridyCreatedToday === undefined) return this.translate.instant('tridys.generation.checkingQuota');
    if (this.validCount > this.remainingTridyCreations) return this.translate.instant('tridys.generation.exceedsQuota', { remaining: this.remainingTridyCreations });
    if (this.headerErrors.length) return this.translate.instant('tridys.generation.fixColumns');
    if (!this.validCount) return this.translate.instant('tridys.generation.noValidRows');
    return undefined;
  }
  get templateDownloadDisabledReason(): string | undefined {
    if (!this.contentTypeId) return this.translate.instant('tridys.importWizard.selectTemplateFirst');
    if (!this.templateHeaders.length) return this.translate.instant('tridys.importWizard.noSchema');
    return undefined;
  }

  async retryQrDownload(): Promise<void> {
    if (!this.createdTridys.length || this.generatingQr || this.filenameTemplateError) return;
    try {
      await this.downloadQrPackage(this.createdTridys);
      this.qrDownloadFailed = false;
    } catch (error: any) {
      const message = await this.getErrorMessage(error);
      console.error('QR export failed', error, message);
      this.commentsService.addSnackBar.emit(message || this.translate.instant('tridys.generation.qrRetryFailed'));
    }
  }

  openTridysList(): void {
    this.router.navigate(['home', this.settingsService.contextId$, 'tridys']);
  }

  openTridysListInNewTab(): void {
    const url = this.router.serializeUrl(this.router.createUrlTree(['home', this.settingsService.contextId$, 'tridys']));
    window.open(url, '_blank', 'noopener');
  }

  startNewGeneration(): void {
    if (this.importing || this.generatingQr) return;
    this.spreadsheet = undefined;
    this.rows = [];
    this.headerErrors = [];
    this.refreshValidationGrid();
    this.importProgress = { completed: 0, total: 0 };
    this.qrProgress = { completed: 0, total: 0 };
    this.createdTridys = [];
    this.qrDownloadFailed = false;
    this.currentStep = 1;
    this.refreshDailyTridyCreateAllowance();
  }

  downloadImportTemplate(format: 'xlsx' | 'csv'): void {
    const headers = this.templateHeaders;
    if (!headers.length || !this.selectedContentType) {
      this.commentsService.addSnackBar.emit(this.templateDownloadDisabledReason || this.translate.instant('tridys.generation.noTemplateAvailable'));
      return;
    }

    const baseName = this.safeFileName(`${this.selectedContentType.displayName || 'dpp'}-import-vorlage`);
    if (format === 'xlsx') {
      const sheet = XLSX.utils.aoa_to_sheet([headers]);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, 'DPP Import');
      const schema = this.selectedContentType?.schema?.properties as SchemaNode[] | undefined;
      const exampleSheet = XLSX.utils.json_to_sheet([this.getTemplateExampleRow(schema || [])], { header: headers });
      XLSX.utils.book_append_sheet(workbook, exampleSheet, 'Example');
      const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${baseName}.xlsx`);
      return;
    }

    const csv = `${headers.map(header => this.csvCell(header)).join(',')}\n`;
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${baseName}.csv`);
  }

  private async downloadQrPackage(created: ImportedTridy[]): Promise<void> {
    this.generatingQr = true;
    this.qrDownloadFailed = false;
    this.qrProgress = { completed: 0, total: created.length };
    try {
      const settings: TridyQrExportSettings = {
        format: this.qrFormat,
        widthMm: Math.max(10, Number(this.qrWidthMm) || 30),
        dpi: Math.max(72, Number(this.qrDpi) || 300),
        errorCorrection: this.qrErrorCorrection,
        quietZoneModules: Math.max(4, Number(this.qrQuietZoneModules) || 4),
        jpegQuality: Math.max(1, Number(this.qrJpegQuality) || 95),
        filenamePrefix: this.qrFilenamePrefix,
        filenameTemplate: this.filenameTemplate
      };
      await this.tridyQrExportService.downloadFromBackend(created, settings);
      this.qrProgress = { completed: created.length, total: created.length };
      this.commentsService.addSnackBar.emit(this.translate.instant('tridys.generation.qrDownloaded'));
    } catch (error) {
      this.qrDownloadFailed = true;
      throw error;
    } finally {
      this.generatingQr = false;
    }
  }

  private async refreshDailyTridyCreateAllowance(): Promise<void> {
    const configuredLimit = Number(this.settingsService.contextAccount$?.limits?.maxTridyCreateCount);
    this.tridyCreateLimit = Number.isFinite(configuredLimit) && configuredLimit >= 0 ? configuredLimit : 1000;
    if (!this.settingsService.contextId$) return;
    this.checkingQuota = true;
    try {
      this.tridyCreatedToday = await this.tridysService.getTodayTridyCreationCount();
    } catch {
      this.tridyCreatedToday = undefined;
    } finally {
      this.checkingQuota = false;
    }
  }

  private get templateHeaders(): string[] {
    const schema = this.selectedContentType?.schema?.properties as SchemaNode[] | undefined;
    return schema?.length ? this.excelImportService.getSpreadsheetTemplateHeaders(schema) : [];
  }

  private getTemplateExampleRow(schema: SchemaNode[]): Record<string, unknown> {
    const row: Record<string, unknown> = {};
    const addValues = (nodes: SchemaNode[], parent = ''): void => {
      nodes.forEach(node => {
        const key = parent ? `${parent}.${node.key}` : node.key;
        if (node.type === 'object') {
          addValues(node.properties || [], key);
          return;
        }
        row[key] = this.getTemplateExampleValue(node);
      });
    };
    addValues(schema);
    return row;
  }

  private getTemplateExampleValue(node: SchemaNode): unknown {
    if (node.type === 'number') return this.getExampleScalarValue(node);
    if (node.type === 'boolean') return true;
    if (node.type === 'date') return this.getExampleScalarValue(node);
    if (node.type === 'array') {
      const item = node.items || { key: 'items', type: 'string' as const };
      if (item.type === 'object') return JSON.stringify([this.getExampleObject(item.properties || [])]);
      const example = this.getExampleScalarValue(item);
      return `${example}; ${example}`;
    }
    return this.getExampleScalarValue(node);
  }

  private getExampleObject(properties: SchemaNode[]): Record<string, unknown> {
    return properties.reduce<Record<string, unknown>>((result, property) => {
      if (property.type === 'object') result[property.key] = this.getExampleObject(property.properties || []);
      else if (property.type === 'array') {
        const item = property.items || { key: 'items', type: 'string' as const };
        result[property.key] = item.type === 'object'
          ? [this.getExampleObject(item.properties || [])]
          : [this.getExampleScalarValue(item)];
      } else result[property.key] = this.getExampleScalarValue(property);
      return result;
    }, {});
  }

  private getExampleScalarValue(node: SchemaNode): string | number | boolean {
    const enumRule = node.validators?.find(rule =>
      (rule['type'] === 'textEnum' || rule['type'] === 'numericEnum') && Array.isArray(rule['values']));
    const firstAllowed = (enumRule?.['values'] as unknown[] | undefined)?.[0];
    if (node.type === 'number') {
      if (typeof firstAllowed === 'number') return firstAllowed;
      const range = node.validators?.find(rule => rule['type'] === 'numericRange');
      const min = range?.['min'];
      const max = range?.['max'];
      const multipleOf = range?.['multipleOf'];
      const step = typeof multipleOf === 'number' && multipleOf > 0
        ? multipleOf : range?.['decimal'] === false ? 1 : 0.01;
      let value = typeof min === 'number' ? min + (range?.['minExclusive'] ? step : 0) : 0;
      if (typeof max === 'number' && value > max) value = max - (range?.['maxExclusive'] ? step : 0);
      if (typeof multipleOf === 'number' && multipleOf > 0) {
        value = Math.ceil(value / multipleOf) * multipleOf;
      }
      return Number(value.toFixed(10));
    }
    if (node.type === 'boolean') return true;
    if (node.type === 'date') return new Date().toISOString().slice(0, 10);
    if (typeof firstAllowed === 'string') return firstAllowed;
    const email = node.validators?.some(rule => rule['type'] === 'textEMail');
    const format = node.validators?.find(rule => rule['type'] === 'textFormat')?.['format'];
    if (email || format === 'email') return 'example@example.com';
    if (format === 'uri' || format === 'url') return 'https://example.com';
    if (format === 'uuid') return '123e4567-e89b-42d3-a456-426614174000';
    if (format === 'date') return new Date().toISOString().slice(0, 10);
    if (format === 'date-time' || format === 'datetime') return new Date().toISOString();
    const length = node.validators?.find(rule => rule['type'] === 'textLength');
    const min = typeof length?.['min'] === 'number' ? length['min'] : 0;
    const max = typeof length?.['max'] === 'number' && length['max'] > 0 ? length['max'] : Infinity;
    const example = `example-${node.key}`;
    return example.padEnd(min, 'x').slice(0, max);
  }

  private csvCell(value: string): string {
    return `"${value.replace(/"/g, '""')}"`;
  }

  private safeFileName(value: string): string {
    return value.toLowerCase().trim()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9._-]/g, '')
      .replace(/-+/g, '-') || 'dpp-import-vorlage';
  }

  private formatGridValue(value: unknown): string {
    if (value === undefined || value === null || value === '') return '';
    if (value instanceof Date) return value.toLocaleDateString(this.dateLocale);
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  }

  private isInvalidGridCell(row: any, header: string, unknownHeaders: Set<string>): boolean {
    if (unknownHeaders.has(header)) return true;
    const errors = this.rowErrors(row);
    return errors.some(error => this.errorBelongsToHeader(error, header));
  }

  private gridCellTooltip(row: any, header: string, value: unknown, unknownHeaders: Set<string>): string {
    if (unknownHeaders.has(header)) return this.translate.instant('tridys.generation.columnNotInSchema', { column: header });
    const errors = this.rowErrors(row).filter(error => this.errorBelongsToHeader(error, header));
    return errors.length ? errors.map(error => this.localizeValidationError(error)).join(' ') : this.formatGridValue(value);
  }

  private rowErrors(row: any): string[] {
    return Array.isArray(row?.__errors) ? row.__errors : [];
  }

  private errorBelongsToHeader(error: string, header: string): boolean {
    const normalizedHeader = header.replace(/\[\]/g, '');
    return error.startsWith(`${header} `)
      || error.startsWith(`${header}.`)
      || error.startsWith(`${header}[`)
      || error.startsWith(`${normalizedHeader} `)
      || error.startsWith(`${normalizedHeader}.`)
      || error.startsWith(`${normalizedHeader}[`);
  }

  private async getErrorMessage(error: any): Promise<string> {
    if (error?.error instanceof Blob) {
      const text = await error.error.text();
      try {
        return JSON.parse(text)?.message || text;
      } catch {
        return text;
      }
    }
    return error?.error?.message || error?.message || '';
  }
}
